import {
	mdiAccount,
	mdiAccountGroup,
	mdiBriefcase,
	mdiCalendar,
	mdiControllerClassicOutline,
	mdiDomain,
	mdiFileDocumentOutline,
	mdiHelpCircleOutline,
	mdiMenuOpen,
	mdiMonitor,
	mdiNewspaperVariantOutline,
	mdiStar,
	mdiSwordCross,
	mdiTrophy,
	mdiVideo,
	mdiVideoOutline,
} from '@mdi/js'

export const SEARCH_TYPE_CONFIG = {
	// ── Community API ───────────────────────────────
	games: {
		label: 'Games',
		icon: mdiControllerClassicOutline,
		source: 'community',
		getDisplayName: item => item.localized_name || item.name,
		getSubtitle: item => {
			const names = item.alternative_names || []
			return names.map(n => (typeof n === 'string' ? n : n.name)).filter(Boolean).join(', ')
		},
		getThumbnail: item => item.thumbnail_url_src || item.thumbnail_url || item.url_cover_src || item.url_cover,
		getRoute: item => ({ name: 'community-wikigamer-games-edit', params: { gameSlug: item.slug } }),
	},
	characters: {
		label: 'Characters',
		icon: mdiAccount,
		source: 'community',
		getDisplayName: item => item.localized_name || item.name,
		getSubtitle: item => {
			const names = item.alternative_names || []
			return names.map(n => (typeof n === 'string' ? n : n.name)).filter(Boolean).join(', ')
		},
		getThumbnail: item => item.thumbnail_url_src || item.thumbnail_url || item.url_image_src || item.url_image,
		getRoute: item => ({ name: 'community-wikigamer-characters-edit', params: { characterSlug: item.slug } }),
	},
	companies: {
		label: 'Companies',
		icon: mdiDomain,
		source: 'community',
		getDisplayName: item => item.name,
		getSubtitle: item => item.fantasy_name || '',
		getThumbnail: item => item.thumbnail_url_src || item.thumbnail_url || item.logo_url_src || item.logo_url,
		getRoute: item => ({ name: 'companies-edit', params: { id: item.id } }),
	},
	platforms: {
		label: 'Platforms',
		icon: mdiMonitor,
		source: 'community',
		getDisplayName: item => item.localized_name || item.name,
		getSubtitle: item => {
			const names = item.alternative_names || []
			return names.map(n => (typeof n === 'string' ? n : n.name)).filter(Boolean).join(', ')
		},
		getThumbnail: item => item.thumbnail_url_src || item.thumbnail_url || item.url_image_src || item.url_image || item.icon_url_src,
		getRoute: item => ({ name: 'community-wikigamer-platforms-edit', params: { slug: item.slug } }),
	},
	pulses: {
		label: 'Pulses',
		icon: mdiNewspaperVariantOutline,
		source: 'community',
		getDisplayName: item => item.title,
		getSubtitle: () => '',
		getThumbnail: item => item.url_image_src || item.url_image || item.url_image_thumb,
		getRoute: item => ({ name: 'community-contents-pulses-edit', params: { link: item.link || item.slug || item.id } }),
	},
	reviews: {
		label: 'Reviews',
		icon: mdiStar,
		source: 'community',
		getDisplayName: item => item.title,
		getSubtitle: () => '',
		getThumbnail: item => item.thumbnail_src || item.thumbnail || item.url_image_src || item.url_image_thumb,
		getRoute: item => ({ name: 'community-contents-reviews-edit', params: { slug: item.slug } }),
	},
	videos: {
		label: 'Videos',
		icon: mdiVideo,
		source: 'community',
		getDisplayName: item => item.title,
		getSubtitle: item => (item.summary || '').replace(/<[^>]*>/g, '').substring(0, 80),
		getThumbnail: item => item.url_image_src || item.url_image || item.url_image_thumb,
		getRoute: item => ({ name: 'community-contents-videos-edit', params: { link: item.link || item.slug || item.id } }),
	},
	users: {
		label: 'Users',
		icon: mdiAccount,
		source: 'community',
		getDisplayName: item => item.nickname || item.name,
		getSubtitle: () => '',
		getThumbnail: item => item.avatar_url_src || item.avatar_url || item.thumbnail_url,
		getRoute: () => null,
	},
	events: {
		label: 'Events',
		icon: mdiCalendar,
		source: 'community',
		getDisplayName: item => item.localized_name || item.name,
		getSubtitle: () => '',
		getThumbnail: item => item.url_image_src || item.url_image || item.thumbnail_url,
		getRoute: item => ({ name: 'comunity-wikigamer-events-edit', params: { id: item.id } }),
	},
	quizzes: {
		label: 'Quizzes',
		icon: mdiHelpCircleOutline,
		source: 'community',
		getDisplayName: item => item.name || item.title,
		getSubtitle: () => '',
		getThumbnail: item => item.url_image_src || item.url_image || item.thumbnail_url,
		getRoute: item => ({ name: 'community-contents-quizzes-edit', params: { id: item.id } }),
	},
	esport_teams: {
		label: 'ESport Teams',
		icon: mdiSwordCross,
		source: 'community',
		getDisplayName: item => item.name,
		getSubtitle: () => '',
		getThumbnail: item => item.thumbnail_url_src || item.thumbnail_url,
		getRoute: () => ({ name: 'community-esports-teams-list' }),
	},

	// ── TV API ──────────────────────────────────────
	streamers: {
		label: 'Streamers',
		icon: mdiVideoOutline,
		source: 'tv',
		getDisplayName: item => item.display_name || item.name,
		getSubtitle: item => [item.provider, item.language].filter(Boolean).join(' · '),
		getThumbnail: item => item.image || item.avatar,
		getRoute: () => ({ name: 'tv-streamers-list' }),
	},

	// ── Jobs API ────────────────────────────────────
	vacancies: {
		label: 'Vagas',
		icon: mdiBriefcase,
		source: 'jobs',
		getDisplayName: item => item.title,
		getSubtitle: item => {
			const company = item.company?.data?.name || item.company?.name || ''
			const location = item.localization || ''
			return [company, location].filter(Boolean).join(' · ')
		},
		getThumbnail: item => item.company?.data?.logo_url_src || item.company?.data?.logo_url || item.company?.logo_url_src || item.company?.logo_url || null,
		getRoute: item => ({ name: 'jobs-vacancies-edit', params: { id: item.id } }),
	},

	// ── Main API (Network) ──────────────────────────
	blog_posts: {
		label: 'Blog Posts',
		icon: mdiFileDocumentOutline,
		source: 'network',
		getDisplayName: item => item.title,
		getSubtitle: item => item.category?.data?.name || item.category?.name || '',
		getThumbnail: item => item.url_image_src || item.url_image || item.image_src || item.image,
		getRoute: item => ({ name: 'blog-posts-edit', params: { id: item.id } }),
	},
	network_companies: {
		label: 'Network Companies',
		icon: mdiDomain,
		source: 'network',
		getDisplayName: item => item.name,
		getSubtitle: item => item.fantasy_name || item.localization || '',
		getThumbnail: item => item.logo_url_src || item.logo_url || null,
		getRoute: item => ({ name: 'companies-edit', params: { id: item.id } }),
	},

	// ── Agency API ──────────────────────────────────
	influencers: {
		label: 'Influencers',
		icon: mdiAccountGroup,
		source: 'agency',
		getDisplayName: item => item.nickname || item.name,
		getSubtitle: item => {
			const followers = item.total_followers
			if (followers) return `${Number(followers).toLocaleString()} followers`
			return ''
		},
		getThumbnail: item => item.image_src || item.image_original_src || item.image || null,
		// The search hits /influencers-professional-info, so item.id is the
		// professional record — the edit screen is keyed by the personal one.
		getRoute: item => ({
			name: 'agency-influencers-edit',
			params: { id: item.influencers_personal_informations_id || item.id },
		}),
	},
	cases: {
		label: 'Cases',
		icon: mdiTrophy,
		source: 'agency',
		getDisplayName: item => item.name,
		getSubtitle: item => item.client_name || '',
		getThumbnail: item => item.hero_image_url_src || item.hero_image_url || null,
		getRoute: item => ({ name: 'agency-marketing-cases-edit', params: { id: item.id } }),
	},
	showcases: {
		label: 'Showcases',
		icon: mdiCalendar,
		source: 'agency',
		getDisplayName: item => item.name,
		getSubtitle: item => item.year ? `${item.year}` : '',
		getThumbnail: () => null,
		getRoute: item => ({ name: 'agency-showcases-edit', params: { id: item.id } }),
	},

	// ── Nav Modules (local, no API) ─────────────────
	nav_modules: {
		label: 'Módulos',
		icon: mdiMenuOpen,
		source: null,
		getDisplayName: item => item.title,
		getSubtitle: item => item.breadcrumb !== item.title ? item.breadcrumb : item.platform,
		getThumbnail: () => null,
		getRoute: item => ({ name: item.routeName }),
	},
}
